import { useCallback } from 'react';

export type AnalyticsFilterResult = {
  shouldApply: boolean;
  shouldClear: boolean;
};

const isEmpty = (value: any): boolean => !value || value === '';

const clearAnalyticsFields = (values: Record<string, any>) => ({
  ...values,
  metric_name: undefined,
  metric_value: undefined,
});

export const shouldApplyAnalyticsFilter = (
  values: Record<string, any>
): AnalyticsFilterResult => {
  const metricName = values.metric_name;
  const metricValue = values.metric_value;

  const hasMetricName = !isEmpty(metricName);
  const hasMetricValue = !isEmpty(metricValue);

  if (!hasMetricName) return { shouldApply: false, shouldClear: true };
  if (!hasMetricValue) return { shouldApply: false, shouldClear: false };
  return { shouldApply: true, shouldClear: false };
};

export const useShouldUpdateImmediately = () => {
  return useCallback((fieldName: string, newValue: any, oldValue: any) => {
    const immediateFields = [
      'metric_name',
      'agent_id',
      'completed_conversations',
      'conversation_channel',
      'started_at',
      'ended_at',
      'countries',
      'participant_metadata',
    ];

    if (immediateFields.includes(fieldName)) return true;

    const isStringField =
      typeof newValue === 'string' && typeof oldValue === 'string';
    const isClearing = newValue === '' && oldValue !== '';

    return isStringField && isClearing;
  }, []);
};

export const useShouldRefreshForm = (uiFilters: Record<string, any>) => {
  return useCallback(
    (newValues: Record<string, any>) => {
      return newValues.metric_name !== uiFilters?.metric_name;
    },
    [uiFilters?.metric_name]
  );
};

export const processAnalyticsFilterForAPI = (
  processedValues: Record<string, any>,
  isMetricNameChange: boolean
): Record<string, any> | null => {
  const { shouldApply, shouldClear } =
    shouldApplyAnalyticsFilter(processedValues);

  if (shouldClear || isMetricNameChange) {
    return clearAnalyticsFields(processedValues);
  }

  if (!shouldApply) {
    const metricName = processedValues.metric_name;
    const metricValue = processedValues.metric_value;

    if (!isEmpty(metricName) && isEmpty(metricValue)) {
      return null;
    }
  }

  return processedValues;
};

export const isMetricNameChanged = (
  newValues: Record<string, any>,
  currentFilters: Record<string, any>
): boolean => {
  return newValues.metric_name !== currentFilters?.metric_name;
};

export const processMetricNameChange = (
  newValues: Record<string, any>,
  isChanged: boolean
): Record<string, any> => {
  return isChanged ? { ...newValues, metric_value: undefined } : newValues;
};
